<script setup>
import SidebarCard from '@/components/cards/SidebarCard.vue';
import AppFooter from '@/components/AppFooter.vue';
import NavTab from '@/components/navigation/NavTab.vue';
import SIdeBarPostList from '@/components/cards/SIdeBarPostList.vue';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import AppHeader from '@/components/headers/AppHeader.vue';
import MainHeaderComponent from '@/components/headers/MainHeaderComponent.vue';
import ImageCard from '@/components/cards/ImageCard.vue';
import { useBusinessStore } from '@/stores/business';
import { computed, watch, onMounted } from 'vue';
import { useMiscStore } from '@/stores/misc';
import { useRoute } from 'vue-router';
import { useContributorStore } from '@/stores/contributor';
import SidebarContributorPostList from '@/components/cards/SidebarContributorPostList.vue';
import TopContributorComponent from '@/components/cards/TopContributorComponent.vue';
import CustomCardImageContent from "@/components/cards/CustomCardImageContent.vue";
import BestOfBestCard from "@/components/cards/BestOfBestCard.vue";

const store = useBusinessStore(),
  misc = useMiscStore(),
  route = useRoute(),
  contributorStore = useContributorStore(),
  currentSector = computed(() => misc.currentSector),
  contributor = computed(() => contributorStore.contributor),
  currentCategory = computed(() => misc.currentCategory);

const fetchData = () => {
  const sector = currentSector.value;
  const category = currentCategory.value;
  const contrib = contributor.value;

  if (sector?.id && category?.id) {
    if (contrib?.id) {
      contributorStore.fetchBusinessesForCategory(contrib.id, sector.id, category.id, route.query.sortBy);
      contributorStore.fetchMostVotedBusinesses(contrib.id, sector.id, category.id);
      contributorStore.fetchTopRatedBusinesses(contrib.id, sector.id, category.id);
    } else {
      store.fetchBusinessesForCategory(sector.id, category.id, route.query.sortBy);
      store.fetchMostVoted(null, category.id);
      store.fetchTopRated(null, category.id);
      store.fetchBestOfVoted(sector.id, category.id);
      store.fetchBestOfRated(sector.id, category.id);
    }
  }
};

watch([currentSector, currentCategory, () => route.query.sortBy], () => {
  fetchData();
});

onMounted(() => {
  misc.fetchCategory(route.params.category);
  misc.updateCurrentSector(route.params.sector);
  if (route.params.username) {
    contributorStore.fetchContributor(route.params.username);
  }
});

contributorStore.fetchPopular('contributions');

let businesses = computed(() => store.businesses),
  topRated = computed(() => store.topRated),
  bestOfRated = computed(() => store.bestOfRated),
  mostVoted = computed(() => store.mostVoted),
  bestOfVoted = computed(() => store.bestOfVoted),
  loading = computed(() => misc.loading),
  loadingTopRated = computed(() => store.loadingTopRated),
  loadingMostVoted = computed(() => store.loadingMostVoted),
  topContributors = computed(() => contributorStore.popular || contributorStore.topRated);

if (route.params.username) {  
  businesses = computed(() => contributorStore.businesses),
    topRated = computed(() => contributorStore.topRatedBusinesses),
    mostVoted = computed(() => contributorStore.mostVotedBusinesses),
    loading = computed(() => misc.loading),
    loadingTopRated = computed(() => contributorStore.loadingTopRatedBusinesses),
    loadingMostVoted = computed(() => contributorStore.loadingMostVotedBusinesses);
}
</script>

<template>
  <DefaultLayout :loading="loading">
    <template #header>
      <AppHeader
        v-if="currentCategory && currentSector"
        :bg-image="currentSector.backgroundImage"
      >
        <template #content>
          <MainHeaderComponent
            :main-title="currentSector.name"
            :title="currentCategory.name"
            :main-title-path="`/${currentSector.slug}`"
            :subtitle="currentCategory.description"
          />
        </template>
      </AppHeader>
    </template>
    <template #main>
      <NavTab
        :businesses="businesses"
        :contributor="contributor"
      />
    </template>
    <template #aside>
      <ImageCard
        v-if="!bestOfRated && !bestOfVoted"
        :image="{ url: currentSector?.backgroundImage, alt: currentSector?.name }"
      />
      <BestOfBestCard
        v-if="bestOfRated"
        :image="{
          url: bestOfRated?.baseImageUrl
        }"
      >
        <template #body>
          <CustomCardImageContent
            type="rated"
            :business="bestOfRated"
          />
        </template>
      </BestOfBestCard>
      <BestOfBestCard
        v-if="bestOfVoted"
        :image="{
          url: bestOfVoted?.baseImageUrl
        }"
      >
        <template #body>
          <CustomCardImageContent
            type="voted"
            :business="bestOfVoted"
          />
        </template>
      </BestOfBestCard>
      <SidebarCard
        :as-content="(mostVoted.length && contributor?.id) > 0 || topRated.length > 0"
        :loading="loadingTopRated"
      >
        <template #header-title>
          Top Rated businesses in {{ currentCategory?.name }}
        </template>
        <template #body>
          <SidebarContributorPostList
            v-if="contributor?.id"
            :businesses="mostVoted"
          />
          <SIdeBarPostList
            v-else
            :businesses="topRated"
          />
        </template>
      </SidebarCard>
      <SidebarCard
        :as-content="mostVoted.length > 0"
        :loading="loadingMostVoted"
      >
        <template #header-title>
          Popular businesses in {{ currentCategory?.name }}
        </template>
        <template #body>
          <SidebarContributorPostList
            v-if="contributor?.id"
            :businesses="mostVoted"
          />
          <SIdeBarPostList
            v-else
            :businesses="mostVoted"
          />
        </template>
      </SidebarCard>
      
      <SidebarCard :as-content="topContributors?.items?.length > 0">
        <template #header-title>
          Top Contributors
        </template>
        <template #body>
          <TopContributorComponent :contributors="topContributors" />
        </template>
        <template #footer>
          <div class="border-t border-gray-200 pt-0.5 pb-2 px-4 text-right text-red-600">
            <router-link
              :to="`/contributors`"
              class="text-xs font-semibold uppercase"
            >
              View all contributors
              <ArrowRightIcon class="inline h-4 w-4 ml-1" />
            </router-link>
          </div>
        </template>
      </SidebarCard>
    </template>
  </DefaultLayout>
  <AppFooter />
</template>

<style scoped></style>
